import { parseApiError } from '../utils/apiErrorParser';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper function to get API role from UI role
const getApiRole = (uiRole) => {
  const roleMapping = {
    'SuperAdmin': 'superadmin',
    'Finance': 'finance',
    'Ops': 'ops',
    'Support': 'support'
  };
  return roleMapping[uiRole] || 'ops';
};

// Helper function to make API requests
const makeRequest = async (endpoint, options = {}) => {
  try {
    const token = localStorage.getItem('authToken');
    
    // Check if token exists
    if (!token) {
      throw new Error('No authentication token found. Please login first.');
    }
    
    const fullUrl = `${BASE_URL}${endpoint}`;
    
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    const requestOptions = {
      method: options.method || 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      signal: controller.signal
    };
    
    // Include body only for POST, PUT, PATCH requests
    if (options.body && ['POST', 'PUT', 'PATCH'].includes(requestOptions.method)) {
      requestOptions.body = options.body;
    }
    
    const response = await fetch(fullUrl, requestOptions);
    
    clearTimeout(timeoutId);

    if (!response.ok) {
      // Handle 404 gracefully - API endpoint might not exist yet
      if (response.status === 404) {
        // Return empty response structure for 404
        return {
          msg: {
            items: [],
            pagination: {
              page: 1,
              items: 10,
              total: 0,
              total_pages: 0
            },
            filters: {
              status_options: [],
              specialization_options: []
            }
          }
        };
      }
      
      const errorText = await response.text();
      let message = '';
      try {
        message = parseApiError(JSON.parse(errorText));
      } catch {
        message = errorText || `Request failed (${response.status})`;
      }
      throw new Error(message);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      // Return empty response for timeout
      return {
        msg: {
          items: [],
          pagination: {
            page: 1,
            items: 10,
            total: 0,
            total_pages: 0
          },
          filters: {
            status_options: [],
            specialization_options: []
          }
        }
      };
    }
    
    // For other errors (network, etc.), return empty response instead of throwing
    // This allows the page to still render with empty state
    console.warn('Doctor API error (showing empty state):', error);
    return {
      msg: {
        items: [],
        pagination: {
          page: 1,
          items: 10,
          total: 0,
          total_pages: 0
        },
        filters: {
          status_options: [],
          specialization_options: []
        }
      }
    };
  }
};

// Get doctors list
export const getDoctors = async (role, params = {}) => {
  const apiRole = getApiRole(role);
  
  // Build query parameters
  const queryParams = new URLSearchParams();
  
  // Add pagination
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.items) queryParams.append('items', params.items.toString());
  
  // Add search
  if (params.search) queryParams.append('search', params.search);
  
  // Date and status filters are not used for application-doctors listing in the Virtual Consultation module,
  // so we intentionally do not append them to the query params here.
  
  // Add specialization filters (can be multiple)
  if (params.specializations && Array.isArray(params.specializations)) {
    params.specializations.forEach(sp => queryParams.append('specializations', sp));
  } else if (params.specializations) {
    queryParams.append('specializations', params.specializations);
  }
  
  const queryString = queryParams.toString();
  const endpoint = queryString 
    ? `/internal/apis/v4/${apiRole}/doctors/list/?${queryString}`
    : `/internal/apis/v4/${apiRole}/doctors/list/`;
  
  const response = await makeRequest(endpoint);
  return response;
};

// Create doctor
// Create doctor
export const createDoctor = async (role, doctorData) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/doctors/create/`;

  const apiPayload = {
    first_name: doctorData.first_name || '',
    last_name: doctorData.last_name || '',

    // backend-required keys
    specializations: Array.isArray(doctorData.specializations)
      ? doctorData.specializations
      : [],

    qualifications: Array.isArray(doctorData.qualifications)
      ? doctorData.qualifications
      : [],

    languages: Array.isArray(doctorData.languages)
      ? doctorData.languages
      : [],

    years_of_experience: doctorData.years_of_experience
      ? Number(doctorData.years_of_experience)
      : null,

    mobile: doctorData.mobile || '',
    email: doctorData.email || '',

    // ✅ ADD ADDRESS (IMPORTANT)
    address: doctorData.address
      ? {
          address_type: 'clinic',
          address_line_1: doctorData.address.address_line_1 || doctorData.address.address_line1 || '',
          address_line_2: doctorData.address.address_line_2 || doctorData.address.address_line2 || '',
          city: doctorData.address.city || '',
          state: doctorData.address.state || '',
          pincode: doctorData.address.pincode || '',
        }
      : null,
  };

  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(apiPayload),
  });
};

// Get doctor availability/slots
export const getDoctorSlots = async (role, doctorId) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/doctors/${doctorId}/availability/`;
  
  return makeRequest(endpoint);
};

// Create doctor slot
export const createDoctorSlot = async (role, doctorId, slotData) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/doctors/${doctorId}/availability/create/`;
  
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(slotData)
  });
};

// Deactivate doctor slot (soft delete)
export const deactivateDoctorSlot = async (role, doctorId, slotId) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/doctors/${doctorId}/availability/${slotId}/deactivate/`;
  
  return makeRequest(endpoint, {
    method: 'PATCH'
  });
};

// Update doctor slot availability
export const updateDoctorSlotAvailability = async (role, doctorId, slotId, slotData) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/doctors/${doctorId}/availability/${slotId}/update/`;
  
  return makeRequest(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(slotData)
  });
};

// ---------------------------------------------------------------------------
// Virtual Consultation — ops on-behalf booking
//
// These functions talk to the new /v4/{ops|superadmin}/... endpoints shipped in
// loan-and-subscription PR #305. Unlike the legacy makeRequest above (which
// soft-returns an empty list on any error), VC create/cancel MUST surface real
// errors so the UI can display them — we don't want an ops user to think a
// booking succeeded when it silently 500-ed. Use makeVcRequest for that.
// ---------------------------------------------------------------------------

const makeVcRequest = async (endpoint, options = {}) => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    throw new Error('No authentication token found. Please login first.');
  }
  const fullUrl = `${BASE_URL}${endpoint}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000);

  const requestOptions = {
    method: options.method || 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
    signal: controller.signal,
  };

  if (options.body && ['POST', 'PUT', 'PATCH'].includes(requestOptions.method)) {
    requestOptions.body = options.body;
  }

  let response;
  try {
    response = await fetch(fullUrl, requestOptions);
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('authRole');
      localStorage.removeItem('anchorId');
      window.location.href = '/login';
      return;
    }
    const errorText = await response.text();
    let message = errorText;
    try {
      const parsed = JSON.parse(errorText);
      // Backend wraps errors as {status: 'error', msg: '...'} or similar.
      message = parsed.msg || parsed.body || parsed.detail || errorText;
    } catch {
      // fallthrough — use raw text
    }
    throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
  }
  return response.json();
};

// Get available VC slots for a given application + date.
// GET /internal/apis/v4/{role}/applications/{application_id}/doctor/available-slots/?date=YYYY-MM-DD
export const getAvailableSlotsForApplication = async (role, applicationId, date) => {
  const apiRole = getApiRole(role);
  const endpoint =
    `/internal/apis/v4/${apiRole}/applications/${applicationId}/doctor/available-slots/?date=${encodeURIComponent(date)}`;
  return makeVcRequest(endpoint);
};

// Get symptoms for the ops VC picker.
// GET /internal/apis/v4/{role}/doctors/symptoms/?search=...
export const getVcSymptoms = async (role, search = '') => {
  const apiRole = getApiRole(role);
  const qs = search ? `?search=${encodeURIComponent(search)}` : '';
  const endpoint = `/internal/apis/v4/${apiRole}/doctors/symptoms/${qs}`;
  return makeVcRequest(endpoint);
};

// Book a VC on behalf of a subscriber.
// POST /internal/apis/v4/{role}/applications/{application_id}/doctor/virtual-consultation/create/
// payload: { member_id, start_at (ISO), end_at (ISO), symptom_ids: [uuid,...] }
export const createVcOnBehalf = async (role, applicationId, payload) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/applications/${applicationId}/doctor/virtual-consultation/create/`;
  return makeVcRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

// Cancel a VC on behalf of a subscriber (ops-booked or subscriber-booked).
// POST /internal/apis/v4/{role}/applications/{application_id}/doctor/virtual-consultation/{consultation_id}/cancel/
// Backend URL was renamed in loan-and-subscription#305 to include /doctor/ for
// convention consistency with the rest of the VC admin routes. The frontend
// fix was pushed to feature/ops-vc-booking as commit 001dd37 but didn't make
// it into PR #69's merge. Re-applied here as part of AP-826.
// asReschedule=true tells the backend to mark the old slot as status='rescheduled'
// (not 'cancelled'), keeping can_cancel=true for the new booking.
export const cancelVcOnBehalf = async (role, applicationId, consultationId, { asReschedule = false } = {}) => {
  const apiRole = getApiRole(role);
  const endpoint =
    `/internal/apis/v4/${apiRole}/applications/${applicationId}/doctor/virtual-consultation/${consultationId}/cancel/`;
  return makeVcRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify({ is_reschedule: asReschedule }),
  });
};

// List VCs for a given application.
// GET /internal/apis/v4/{role}/applications/{application_id}/doctor/virtual-consultations/?status=...
export const getApplicationVcs = async (role, applicationId, params = {}) => {
  const apiRole = getApiRole(role);
  const qs = new URLSearchParams();
  if (params.status) qs.append('status', params.status);
  const query = qs.toString();
  const endpoint = query
    ? `/internal/apis/v4/${apiRole}/applications/${applicationId}/doctor/virtual-consultations/?${query}`
    : `/internal/apis/v4/${apiRole}/applications/${applicationId}/doctor/virtual-consultations/`;

  return makeVcRequest(endpoint);
};

// Get application-doctors list (Virtual Consultation)
export const getApplicationDoctors = async (role, params = {}) => {
  const apiRole = getApiRole(role);
  
  // Build query parameters
  const queryParams = new URLSearchParams();
  
  // Add pagination
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.items) queryParams.append('items', params.items.toString());
  
  // Add search
  if (params.search) queryParams.append('search', params.search);
  
  // Add date filter
  if (params.date_filter) {
    queryParams.append('date_filter', params.date_filter);
    if (params.date_filter === 'custom' && params.start_date && params.end_date) {
      queryParams.append('start_date', params.start_date);
      queryParams.append('end_date', params.end_date);
    }
  }
  
  // Add status filters (can be multiple)
  if (params.status && Array.isArray(params.status)) {
    params.status.forEach(status => queryParams.append('status', status));
  } else if (params.status) {
    queryParams.append('status', params.status);
  }
  
  const queryString = queryParams.toString();
  const endpoint = queryString 
    ? `/internal/apis/v4/${apiRole}/application-doctors/list/?${queryString}`
    : `/internal/apis/v4/${apiRole}/application-doctors/list/`;
  
  const response = await makeRequest(endpoint);
  return response;
};
